import { useState } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import { useLocation } from "wouter";
import { Bell, Check, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { ScrollArea } from "@/components/ui/scroll-area";
import { apiRequest, queryClient } from "@/lib/queryClient";
import { normalizePage } from "@/lib/pagination";
import { getNotificationDestination } from "@/lib/notification-navigation";

interface Notification {
  id: string;
  type: string;
  title: string;
  message: string;
  link?: string;
  read: boolean;
  createdAt: string;
}

type NotificationsResponse = { notifications: Notification[]; unread: number } | Notification[];
const PREVIEW_PAGE_SIZE = 12;

export default function NotificationBell() {
  const [open, setOpen] = useState(false);
  const [, setLocation] = useLocation();

  const { data } = useQuery<NotificationsResponse>({
    queryKey: ["/api/notifications", "preview"],
    queryFn: async () => {
      const response = await fetch(`/api/notifications?page=1&pageSize=${PREVIEW_PAGE_SIZE}`, { credentials: "include" });
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      return response.json();
    },
    refetchInterval: 60000,
  });

  const markAll = useMutation({
    mutationFn: async () => {
      await apiRequest("POST", "/api/notifications/read-all");
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["/api/notifications"] }),
  });

  const clearAll = useMutation({
    mutationFn: async () => {
      await apiRequest("DELETE", "/api/notifications");
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["/api/notifications"] }),
  });

  const markOne = useMutation({
    mutationFn: async (id: string) => {
      await apiRequest("POST", `/api/notifications/${id}/read`);
    },
    onMutate: async (id: string) => {
      await queryClient.cancelQueries({ queryKey: ["/api/notifications"] });
      const previous = queryClient.getQueryData<NotificationsResponse>(["/api/notifications", "preview"]);
      queryClient.setQueryData<NotificationsResponse>(["/api/notifications", "preview"], (current) => {
        if (!current) return current;
        if (Array.isArray(current)) {
          return current.map((notification) =>
            notification.id === id ? { ...notification, read: true } : notification,
          );
        }
        const wasUnread = current.notifications.some((notification) => notification.id === id && !notification.read);
        return {
          ...current,
          unread: wasUnread ? Math.max(0, current.unread - 1) : current.unread,
          notifications: current.notifications.map((notification) =>
            notification.id === id ? { ...notification, read: true } : notification,
          ),
        };
      });
      return { previous };
    },
    onError: (_error, _id, context) => {
      if (context?.previous) {
        queryClient.setQueryData(["/api/notifications", "preview"], context.previous);
      }
    },
    onSettled: () => queryClient.invalidateQueries({ queryKey: ["/api/notifications"] }),
  });

  // This menu is intentionally a small, bounded preview. Full history lives
  // in Notifications Center where it can be paged.
  const items = normalizePage<Notification>(data ?? [], "notifications", 1, PREVIEW_PAGE_SIZE).items.slice(0, PREVIEW_PAGE_SIZE);
  const unread = !Array.isArray(data) ? data?.unread || 0 : 0;

  const onClick = (n: Notification) => {
    if (!n.read) markOne.mutate(n.id);
    setOpen(false);
    setLocation(getNotificationDestination(n));
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="ghost"
          size="icon"
          className="relative h-10 w-10 rounded-full"
          data-testid="button-notifications"
        >
          <Bell className="w-5 h-5" />
          {unread > 0 && (
            <Badge className="absolute -top-1 -right-1 h-4 min-w-4 px-1 text-[10px] bg-red-500 text-white border-0">
              {unread}
            </Badge>
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent align="end" className="flex w-80 max-w-[calc(100vw-1rem)] flex-col overflow-hidden p-0">
        <div className="flex items-center justify-between p-3 border-b">
          <div className="font-semibold text-sm">Notifications</div>
          <div className="flex gap-1">
            <Button variant="ghost" size="sm" onClick={() => markAll.mutate()} disabled={!unread} title="Mark all read">
              <Check className="w-3.5 h-3.5" />
            </Button>
            <Button variant="ghost" size="sm" onClick={() => clearAll.mutate()} disabled={!items.length} title="Clear all">
              <Trash2 className="w-3.5 h-3.5" />
            </Button>
          </div>
        </div>
        <ScrollArea
          className="h-[min(20rem,calc(100vh-10rem))] min-h-24"
          data-testid="notification-list-scroll-area"
          aria-label="Notification list"
          tabIndex={0}
        >
          {items.length === 0 ? (
            <div className="p-6 text-center text-sm text-muted-foreground">No notifications yet</div>
          ) : (
            <div className="divide-y">
              {items.map((n) => (
                <button
                  key={n.id}
                  onClick={() => onClick(n)}
                  className={`w-full border-l-4 p-3 text-left transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-ring ${
                    !n.read
                      ? "border-l-violet-500 bg-violet-100/80 hover:bg-violet-100 dark:bg-violet-950/50 dark:hover:bg-violet-950/70"
                      : "border-l-transparent bg-background/40 text-muted-foreground hover:bg-muted/50"
                  }`}
                  data-testid={`notification-${n.id}`}
                  aria-label={`${n.read ? "Read" : "Unread"} notification: ${n.title}`}
                >
                  <div className="flex items-start gap-2">
                    <div
                      aria-hidden="true"
                      className={`mt-1.5 h-2 w-2 flex-shrink-0 rounded-full ${
                        n.read ? "bg-muted-foreground/30" : "bg-violet-600 ring-2 ring-violet-500/25 dark:bg-violet-400"
                      }`}
                    />
                    <div className="flex-1 min-w-0">
                      <div className={`truncate text-sm ${n.read ? "font-normal text-muted-foreground" : "font-semibold text-foreground"}`}>
                        {n.title}
                      </div>
                      <div className="text-xs text-muted-foreground line-clamp-2 mt-0.5">{n.message}</div>
                      <div className="text-[10px] text-muted-foreground/60 mt-1">
                        {new Date(n.createdAt).toLocaleString()}
                      </div>
                    </div>
                  </div>
                </button>
              ))}
            </div>
          )}
        </ScrollArea>
        <div className="border-t p-2">
          <Button
            variant="ghost"
            size="sm"
            className="w-full text-xs"
            onClick={() => { setOpen(false); setLocation("/notifications"); }}
            data-testid="link-view-all-notifications"
          >
            View all notifications
          </Button>
        </div>
      </PopoverContent>
    </Popover>
  );
}
